'use client';

import React, { useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';

function useScrollReveal() {
  useEffect(() => {
    const elements = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

const socialStats = [
  {
    platform: 'Instagram',
    followers: '1,300',
    detail: 'Up to 50,000 views per post',
    color: 'from-pink-900/40 to-red-900/30',
    border: 'border-pink-800/30',
  },
  {
    platform: 'TikTok',
    followers: '1,400',
    detail: 'Active content creator',
    color: 'from-slate-900/60 to-zinc-900/40',
    border: 'border-slate-700/30',
  },
  {
    platform: 'YouTube',
    followers: 'Growing',
    detail: '@Diggity.Glover',
    color: 'from-red-950/50 to-stone-900/40',
    border: 'border-red-900/30',
  },
];

const videos = [
  {
    label: 'Drum Cover — Boundaries "Death Will Follow Me"',
    url: 'https://www.youtube.com/embed/XP_srzUoBz0',
  },
  { label: 'Drum Cam', url: 'https://www.youtube.com/embed/2l6OQFlcLWw' },
  { label: 'Live Performance', url: 'https://www.youtube.com/embed/O2YEO4FFa7s' },
  { label: 'Content Reel', url: 'https://www.youtube.com/embed/3nU6wFoxTxo' },
];

const bands = [
  {
    name: 'Covert UK',
    genre: 'Metalcore / Hardcore',
    desc: 'UK metalcore/hardcore outfit with a heavy live presence and growing fanbase.',
    spotifyUrl: 'https://open.spotify.com/artist/1c5OT4JPhrGXQ7ynfFW18B?si=ynt4U1b3Rx62cjApOK2nEg',
  },
  {
    name: 'Dystopian Sun',
    genre: 'Deathcore',
    desc: 'Atmospheric heavy music pushing boundaries in the UK metal scene.',
    spotifyUrl: 'https://open.spotify.com/artist/6cCjJqMNxAs1JYk10nFEcX?si=bxR7Sp46Roi03ecZTewgNg',
  },
];

const services = [
  {
    title: 'Session Drumming',
    desc: 'Available for studio and live session work worldwide',
  },
  {
    title: 'Audio Engineering',
    desc: 'Production, mixing and drum programming services',
  },
  {
    title: 'Drum Education',
    desc: 'Free drum programming course available via Whop',
  },
];

const gearSlots = [
  { category: 'Drums', item: 'SJC Custom — Pathfinder Series' },
  { category: 'Cymbals', item: 'Meinl — Byzance & Classics Custom' },
  { category: 'Pedals', item: 'Axis Longboard' },
  { category: 'Triggers', item: 'OnTrigger' },
];

const performanceCredits = [
  { credit: 'Covert UK', role: 'Drummer', type: 'Band / Live & Studio' },
  { credit: 'Dystopian Sun', role: 'Drummer', type: 'Band / Live & Studio' },
  { credit: 'Session Work', role: 'Session Drummer', type: 'Studio / Remote' },
  { credit: 'Wish', role: 'Session Drummer', type: '' },
  { credit: 'Remote Drum Production', role: 'Drummer / Engineer', type: 'Worldwide / Online' },
];

export default function EPK() {
  useScrollReveal();

  return (
    <div
      style={{ minHeight: '100vh', background: '#000000', color: '#ffffff', overflowX: 'hidden' }}
    >
      {/* Noise grain overlay */}
      <div
        className="fixed inset-0 pointer-events-none z-50"
        style={{
          opacity: 0.035,
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E\")",
          backgroundRepeat: 'repeat',
          backgroundSize: '128px 128px',
        }}
      />

      {/* ═══════════════════════════════════════════════════════
           1. HERO
        ═══════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden" style={{ minHeight: '100vh' }}>
        <div className="absolute inset-0" style={{ background: '#0D0D0D' }}>
          <video
            autoPlay
            loop
            muted
            playsInline
            disablePictureInPicture
            className="absolute inset-0 w-full h-full object-cover object-center"
            style={{ opacity: 0.35 }}
          >
            <source src="https://baxwbiwdwyhamsmlrffg.supabase.co/storage/v1/object/public/media/epk-bg.mp4" type="video/mp4" />
          </video>
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(to bottom, rgba(13,13,13,0.55) 0%, rgba(13,13,13,0.75) 60%, #0D0D0D 100%)',
            }}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(ellipse 70% 50% at 20% 60%, rgba(180,0,0,0.22) 0%, transparent 65%)',
            }}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage:
                'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(180,0,0,0.012) 3px, rgba(180,0,0,0.012) 4px)',
            }}
          />
          {/* Video credit */}
          <a
            href="https://www.rjfhmusic.co.uk"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              position: 'absolute',
              bottom: '12px',
              right: '16px',
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.6rem',
              letterSpacing: '0.08em',
              color: 'rgba(255,255,255,0.35)',
              textDecoration: 'none',
              zIndex: 5,
              transition: 'color 0.2s',
            }}
            onMouseEnter={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.7)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.35)')}
          >
            Filmed by RJFH Music
          </a>
        </div>

        <div
          className="relative z-10 max-w-6xl mx-auto px-6 flex flex-col justify-center"
          style={{ minHeight: '100vh', paddingTop: '6rem', paddingBottom: '5rem' }}
        >
          <div className="max-w-3xl">
            <span
              className="inline-block font-mono text-xs tracking-widest uppercase mb-6"
              style={{ color: '#CC0000', letterSpacing: '0.2em' }}
            >
              // Electronic Press Kit
            </span>

            <h1
              className="font-display font-extrabold uppercase text-white mb-5"
              style={{
                fontSize: 'clamp(3.5rem, 10vw, 7.5rem)',
                lineHeight: '0.88',
                letterSpacing: '-0.03em',
              }}
            >
              Diggity
              <br />
              <span
                style={{
                  background: 'linear-gradient(135deg, #FF3333 0%, #CC0000 50%, #8B0000 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Glover
              </span>
            </h1>

            <p
              className="font-body text-lg mb-10"
              style={{ color: '#A0A0A0', letterSpacing: '0.02em' }}
            >
              Session Drummer&nbsp;·&nbsp;Audio Engineer&nbsp;·&nbsp;Manchester, UK
            </p>

            <div className="flex flex-wrap gap-4">
              <a
                href="https://fiverr.com/diggityglover"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary px-8 py-4 text-sm font-semibold tracking-wide"
                style={{ fontSize: '0.875rem' }}
              >
                Book / Hire Me
              </a>
              <a
                href="mailto:diggityglover@gmail.com"
                className="btn-outline px-8 py-4 text-sm font-semibold tracking-wide"
                style={{ fontSize: '0.875rem' }}
              >
                Email Enquiry
              </a>
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
          style={{ background: 'linear-gradient(to bottom, transparent, #0D0D0D)' }}
        />
      </section>

      {/* ═══════════════════════════════════════════════════════
           2. ARTIST BIO
        ═══════════════════════════════════════════════════════ */}
      <section className="py-24" style={{ borderBottom: '1px solid rgba(180,0,0,0.12)' }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="reveal grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-3">
              <span className="section-label block mb-6">// Artist Bio</span>
              <h2
                className="font-display font-extrabold uppercase text-white mb-8"
                style={{
                  fontSize: 'clamp(2rem, 4vw, 3rem)',
                  lineHeight: '1.02',
                  letterSpacing: '-0.02em',
                }}
              >
                Manchester-Based
                <br />
                <span style={{ color: '#CC0000' }}>Session Drummer</span>
              </h2>
              <div
                className="space-y-5 font-body leading-relaxed"
                style={{ color: '#A0A0A0', fontSize: '1.05rem' }}
              >
                <p>
                  Diggity Glover is a Manchester-based session drummer, gigging musician and audio
                  engineer.
                </p>
                <p>
                  As the drummer for metalcore/hardcore outfit Covert UK and previous deathcore act
                  Dystopian Sun, Diggity has built a reputation on the UK heavy music circuit for
                  delivering explosive live performances and locked-in studio recordings. His dual
                  background as an audio engineer means he doesn&apos;t just play the drums, he
                  understands how they sit in a mix, how to capture them and how to make them hit
                  harder on record than most drummers ever manage live.
                </p>
                <p>
                  Available for session work, touring and remote drum production worldwide, Diggity
                  brings the same relentless energy to every project, whether he&apos;s behind the
                  kit in a rehearsal room in Manchester or tracking remotely for artists across the
                  globe. He&apos;s not just a drummer for hire; he&apos;s a collaborator who elevates
                  the music around him.
                </p>
              </div>

              <div className="flex flex-wrap gap-3 mt-8">
                {['Covert UK', 'Dystopian Sun'].map((band) => (
                  <span
                    key={band}
                    className="font-mono text-xs tracking-widest uppercase px-4 py-2"
                    style={{
                      border: '1px solid rgba(204,0,0,0.35)',
                      color: '#CC0000',
                      borderRadius: '2px',
                      background: 'rgba(204,0,0,0.06)',
                    }}
                  >
                    {band}
                  </span>
                ))}
              </div>
            </div>

            <div className="lg:col-span-2 reveal">
              <div
                className="relative overflow-hidden"
                style={{ borderRadius: '3px', border: '1px solid rgba(204,0,0,0.2)' }}
              >
                <AppImage
                  src="/assets/images/diggity-14.jpg"
                  alt="Diggity Glover portrait photo"
                  width={600}
                  height={700}
                  className="w-full object-cover"
                  style={{ filter: 'grayscale(15%) contrast(1.08)' }}
                />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: 'linear-gradient(to top, rgba(180,0,0,0.18) 0%, transparent 50%)',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
           3. PHOTO PACKAGE
        ═══════════════════════════════════════════════════════ */}
      <section
        className="py-24"
        style={{ background: '#0A0A0A', borderBottom: '1px solid rgba(180,0,0,0.12)' }}
      >
        <div className="max-w-6xl mx-auto px-6">
          <div className="reveal mb-4">
            <span className="section-label block mb-3">// Photo Package</span>
            <h2
              className="font-display font-extrabold uppercase text-white mb-2"
              style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1',
                letterSpacing: '-0.02em',
              }}
            >
              Press Photos
            </h2>
            <p className="font-mono text-xs tracking-wide mb-10" style={{ color: '#555' }}>
              All images available at 300dpi+ for print and digital use. Contact to request full
              resolution files.
            </p>
          </div>

          {/* Main headshot + live action grid */}
          <div className="reveal grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Professional Headshot — large */}
            <div
              className="md:col-span-1 relative overflow-hidden"
              style={{
                borderRadius: '3px',
                border: '1px solid rgba(204,0,0,0.2)',
                minHeight: '360px',
                background: '#111',
              }}
            >
              <AppImage
                src="/assets/images/diggity-12.jpg"
                alt="Diggity Glover professional headshot"
                fill
                className="object-cover object-top"
                style={{ filter: 'grayscale(10%) contrast(1.05)' }}
              />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: 'linear-gradient(to top, rgba(13,13,13,0.85) 0%, transparent 50%)',
                }}
              />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div
                  className="font-mono text-xs tracking-widest uppercase"
                  style={{ color: '#CC0000' }}
                >
                  Professional Headshot
                </div>
                <div className="font-mono text-xs mt-1" style={{ color: '#555' }}>
                  High Resolution · 300dpi+
                </div>
              </div>
            </div>

            {/* Live action shots */}
            <div className="md:col-span-2 grid grid-rows-2 gap-4">
              <div
                className="relative overflow-hidden"
                style={{
                  borderRadius: '3px',
                  border: '1px solid rgba(204,0,0,0.2)',
                  minHeight: '170px',
                  background: '#111',
                }}
              >
                <AppImage
                  src="/assets/images/diggity-6.jpg"
                  alt="Diggity Glover live action shot behind the drum kit"
                  fill
                  className="object-cover object-center"
                  style={{ filter: 'grayscale(10%) contrast(1.08)', opacity: 0.9 }}
                />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: 'linear-gradient(to top, rgba(13,13,13,0.8) 0%, transparent 55%)',
                  }}
                />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div
                    className="font-mono text-xs tracking-widest uppercase"
                    style={{ color: '#CC0000' }}
                  >
                    Live Action — Behind the Kit
                  </div>
                  <div className="font-mono text-xs mt-1" style={{ color: '#555' }}>
                    High Resolution · 300dpi+
                  </div>
                </div>
              </div>

              <div
                className="relative overflow-hidden flex items-center justify-center"
                style={{
                  borderRadius: '3px',
                  border: '1px dashed rgba(204,0,0,0.2)',
                  minHeight: '170px',
                  background: '#111',
                }}
              >
                <div className="text-center p-6">
                  <div
                    className="w-12 h-12 mx-auto mb-3 flex items-center justify-center"
                    style={{ border: '1px dashed rgba(204,0,0,0.3)', borderRadius: '3px' }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#CC0000"
                      strokeWidth="1.5"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <path d="M21 15l-5-5L5 21" />
                    </svg>
                  </div>
                  <div
                    className="font-mono text-xs tracking-widest uppercase mb-1"
                    style={{ color: '#CC0000' }}
                  >
                    Live Action — Gear Shot
                  </div>
                  <div className="font-mono text-xs" style={{ color: '#444' }}>
                    High-res photo pending · Contact to request
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Behind the scenes / candid row */}
          <div className="reveal grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              className="relative overflow-hidden flex items-center justify-center"
              style={{
                borderRadius: '3px',
                border: '1px dashed rgba(204,0,0,0.2)',
                minHeight: '200px',
                background: '#111',
              }}
            >
              <div className="text-center p-6">
                <div
                  className="w-12 h-12 mx-auto mb-3 flex items-center justify-center"
                  style={{ border: '1px dashed rgba(204,0,0,0.3)', borderRadius: '3px' }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#CC0000"
                    strokeWidth="1.5"
                  >
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                </div>
                <div
                  className="font-mono text-xs tracking-widest uppercase mb-1"
                  style={{ color: '#CC0000' }}
                >
                  Behind the Scenes
                </div>
                <div className="font-mono text-xs" style={{ color: '#444' }}>
                  Candid / Studio shot · Contact to request
                </div>
              </div>
            </div>

            <div
              className="relative overflow-hidden flex items-center justify-center"
              style={{
                borderRadius: '3px',
                border: '1px dashed rgba(204,0,0,0.2)',
                minHeight: '200px',
                background: '#111',
              }}
            >
              <div className="text-center p-6">
                <div
                  className="w-12 h-12 mx-auto mb-3 flex items-center justify-center"
                  style={{ border: '1px dashed rgba(204,0,0,0.3)', borderRadius: '3px' }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#CC0000"
                    strokeWidth="1.5"
                  >
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                </div>
                <div
                  className="font-mono text-xs tracking-widest uppercase mb-1"
                  style={{ color: '#CC0000' }}
                >
                  Candid / Off-Stage
                </div>
                <div className="font-mono text-xs" style={{ color: '#444' }}>
                  Behind-the-scenes shot · Contact to request
                </div>
              </div>
            </div>
          </div>

          <div className="reveal mt-6">
            <div
              className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
              style={{
                background: 'rgba(204,0,0,0.05)',
                border: '1px solid rgba(204,0,0,0.15)',
                borderRadius: '3px',
              }}
            >
              <p className="font-mono text-xs tracking-wide" style={{ color: '#888' }}>
                Full resolution photo package available on request — all images 300dpi+, print and
                digital ready.
              </p>
              <a
                href="mailto:diggityglover@gmail.com?subject=EPK Photo Request"
                className="font-mono text-xs tracking-widest uppercase px-5 py-2.5 whitespace-nowrap transition-all duration-200"
                style={{
                  border: '1px solid rgba(204,0,0,0.4)',
                  color: '#CC0000',
                  borderRadius: '2px',
                  background: 'transparent',
                }}
              >
                Request Photos →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
           4. SOCIAL REACH & STATS
        ═══════════════════════════════════════════════════════ */}
      <section className="py-24" style={{ borderBottom: '1px solid rgba(180,0,0,0.12)' }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="reveal mb-12">
            <span className="section-label block mb-3">// Social Media &amp; Reach</span>
            <h2
              className="font-display font-extrabold uppercase text-white"
              style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1',
                letterSpacing: '-0.02em',
              }}
            >
              Audience &amp; Reach
            </h2>
          </div>

          <div className="reveal grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {socialStats.map((s) => (
              <div
                key={s.platform}
                className={`relative overflow-hidden p-7 bg-gradient-to-br ${s.color}`}
                style={{
                  border: `1px solid`,
                  borderColor: s.border.replace('border-', '').replace('/30', ''),
                  borderRadius: '3px',
                }}
              >
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{ border: '1px solid rgba(255,255,255,0.04)', borderRadius: '3px' }}
                />
                <div
                  className="font-mono text-xs tracking-widest uppercase mb-2"
                  style={{ color: '#666' }}
                >
                  {s.platform}
                </div>
                <div
                  className="font-display font-extrabold mb-2"
                  style={{ fontSize: 'clamp(2rem, 4vw, 2.8rem)', lineHeight: '1', color: '#fff' }}
                >
                  {s.followers}
                </div>
                <div className="font-mono text-xs" style={{ color: '#888' }}>
                  {s.detail}
                </div>
              </div>
            ))}
          </div>

          <div className="reveal flex flex-col sm:flex-row sm:items-center gap-4">
            <div
              className="flex-1 p-5"
              style={{
                background: 'rgba(204,0,0,0.06)',
                border: '1px solid rgba(204,0,0,0.2)',
                borderRadius: '3px',
              }}
            >
              <p className="font-mono text-xs tracking-wide" style={{ color: '#A0A0A0' }}>
                Also active on <span style={{ color: '#CC0000' }}>Facebook</span> —{' '}
                <span style={{ color: '#ccc' }}>
                  Organic reach consistently exceeds follower count — content regularly pushed by
                  the algorithm to new audiences.
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
           5. VIDEO / MEDIA
        ═══════════════════════════════════════════════════════ */}
      <section
        className="py-24"
        style={{ background: '#0A0A0A', borderBottom: '1px solid rgba(180,0,0,0.12)' }}
      >
        <div className="max-w-6xl mx-auto px-6">
          <div className="reveal mb-12">
            <span className="section-label block mb-3">// Video Content</span>
            <h2
              className="font-display font-extrabold uppercase text-white"
              style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1',
                letterSpacing: '-0.02em',
              }}
            >
              Video &amp; Performance
            </h2>
          </div>

          {/* Featured: MURDER8 full drum cover */}
          <div
            className="reveal mb-10 grid grid-cols-1 md:grid-cols-5 gap-0 overflow-hidden"
            style={{
              borderRadius: '3px',
              border: '1px solid rgba(204,0,0,0.35)',
              background: '#111',
            }}
          >
            <div className="relative md:col-span-3" style={{ aspectRatio: '16/9' }}>
              <iframe
                src="https://www.youtube.com/embed/2pkBXutnSAA"
                title="MURDER8 by Bodysnatcher — full drum cover by Diggity Glover"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
                style={{ border: 'none' }}
              />
            </div>
            <div className="md:col-span-2 p-8 flex flex-col justify-center">
              <span
                style={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.65rem',
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: '#ff1a1a',
                  marginBottom: '12px',
                  display: 'block',
                }}
              >
                // Featured — New Release
              </span>
              <h3
                className="font-display font-extrabold uppercase text-white"
                style={{ fontSize: 'clamp(1.3rem, 2.5vw, 1.8rem)', lineHeight: 1.1, marginBottom: '14px' }}
              >
                MURDER8 — Bodysnatcher
                <span style={{ display: 'block', color: '#ff1a1a' }}>Full Drum Cover</span>
              </h3>
              <p
                className="font-body"
                style={{ fontSize: '0.9rem', lineHeight: 1.7, color: '#cccccc', marginBottom: '18px' }}
              >
                A full-length, single-take drum cover of one of modern deathcore&apos;s most
                punishing tracks — blast beats, breakdowns and double-kick endurance, played
                clean and captured live on camera. Every video puts the kit, cymbals and
                hardware front and centre for the audience that buys them.
              </p>
              <a
                href="https://www.youtube.com/watch?v=2pkBXutnSAA"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
                style={{ fontSize: '0.75rem', padding: '10px 22px', alignSelf: 'flex-start' }}
              >
                Watch on YouTube
              </a>
            </div>
          </div>

          <div className="reveal grid grid-cols-1 md:grid-cols-2 gap-5">
            {videos.map((v) => (
              <div
                key={v.label}
                className="relative overflow-hidden"
                style={{
                  borderRadius: '3px',
                  border: '1px solid rgba(204,0,0,0.2)',
                  background: '#111',
                }}
              >
                <div className="relative" style={{ aspectRatio: '16/9' }}>
                  <iframe
                    src={v.url}
                    title={v.label}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                    style={{ border: 'none' }}
                  />
                </div>
                <div className="p-4">
                  <div className="font-display font-bold text-white text-sm">{v.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
           6. MUSIC & DISCOGRAPHY
        ═══════════════════════════════════════════════════════ */}
      <section className="py-24" style={{ borderBottom: '1px solid rgba(180,0,0,0.12)' }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="reveal mb-12">
            <span className="section-label block mb-3">// Music &amp; Discography</span>
            <h2
              className="font-display font-extrabold uppercase text-white"
              style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1',
                letterSpacing: '-0.02em',
              }}
            >
              Bands &amp; Projects
            </h2>
          </div>

          <div className="reveal grid grid-cols-1 md:grid-cols-2 gap-6">
            {bands.map((band) => (
              <div
                key={band.name}
                className="relative overflow-hidden p-8 group"
                style={{
                  background: 'linear-gradient(135deg, #161616 0%, #111 100%)',
                  border: '1px solid rgba(204,0,0,0.2)',
                  borderRadius: '3px',
                }}
              >
                <div
                  className="absolute top-0 left-0 right-0 h-px"
                  style={{
                    background:
                      'linear-gradient(to right, transparent, rgba(204,0,0,0.6), transparent)',
                  }}
                />
                <div
                  className="font-mono text-xs tracking-widest uppercase mb-3"
                  style={{ color: '#CC0000' }}
                >
                  {band.genre}
                </div>
                <h3
                  className="font-display font-extrabold uppercase text-white mb-4"
                  style={{
                    fontSize: 'clamp(1.6rem, 3vw, 2.2rem)',
                    lineHeight: '1',
                    letterSpacing: '-0.02em',
                  }}
                >
                  {band.name}
                </h3>
                <p className="font-body text-sm leading-relaxed mb-6" style={{ color: '#888' }}>
                  {band.desc}
                </p>
                <a
                  href={band.spotifyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-xs tracking-widest uppercase px-5 py-2.5 inline-block transition-all duration-200"
                  style={{
                    border: '1px solid rgba(204,0,0,0.4)',
                    color: '#CC0000',
                    borderRadius: '2px',
                    background: 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(204,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
                  }}
                >
                  Listen on Spotify →
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
           7. PERFORMANCE HISTORY & NOTABLE CREDITS
        ═══════════════════════════════════════════════════════ */}
      <section
        className="py-24"
        style={{ background: '#0A0A0A', borderBottom: '1px solid rgba(180,0,0,0.12)' }}
      >
        <div className="max-w-6xl mx-auto px-6">
          <div className="reveal mb-12">
            <span className="section-label block mb-3">// Performance History</span>
            <h2
              className="font-display font-extrabold uppercase text-white mb-3"
              style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1',
                letterSpacing: '-0.02em',
              }}
            >
              Notable Credits
            </h2>
            <p className="font-body text-sm" style={{ color: '#666' }}>
              Tours, residencies, notable artists, sessions, and live appearances.
            </p>
          </div>

          <div
            className="reveal overflow-hidden"
            style={{ border: '1px solid rgba(204,0,0,0.15)', borderRadius: '3px' }}
          >
            <table className="w-full">
              <thead>
                <tr
                  style={{
                    background: 'rgba(204,0,0,0.08)',
                    borderBottom: '1px solid rgba(204,0,0,0.2)',
                  }}
                >
                  <th
                    className="text-left p-4 font-mono text-xs tracking-widest uppercase"
                    style={{ color: '#CC0000' }}
                  >
                    Credit
                  </th>
                  <th
                    className="text-left p-4 font-mono text-xs tracking-widest uppercase"
                    style={{ color: '#CC0000' }}
                  >
                    Role
                  </th>
                  <th
                    className="text-left p-4 font-mono text-xs tracking-widest uppercase hidden md:table-cell"
                    style={{ color: '#CC0000' }}
                  >
                    Type
                  </th>
                </tr>
              </thead>
              <tbody>
                {performanceCredits.map((item, i) => (
                  <tr
                    key={item.credit}
                    style={{
                      borderBottom:
                        i < performanceCredits.length - 1
                          ? '1px solid rgba(255,255,255,0.04)'
                          : 'none',
                      background: i % 2 === 0 ? '#111' : '#0D0D0D',
                    }}
                  >
                    <td className="p-4 font-body font-semibold text-white text-sm">
                      {item.credit}
                    </td>
                    <td className="p-4 font-mono text-xs" style={{ color: '#888' }}>
                      {item.role}
                    </td>
                    <td
                      className="p-4 font-mono text-xs hidden md:table-cell"
                      style={{ color: '#555' }}
                    >
                      {item.type}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
           8. PRESS & MEDIA COVERAGE
        ═══════════════════════════════════════════════════════ */}
      <section className="py-24" style={{ borderBottom: '1px solid rgba(180,0,0,0.12)' }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="reveal mb-12">
            <span className="section-label block mb-3">// Press &amp; Media</span>
            <h2
              className="font-display font-extrabold uppercase text-white mb-3"
              style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1',
                letterSpacing: '-0.02em',
              }}
            >
              For Press
            </h2>
            <p className="font-body text-sm" style={{ color: '#666' }}>
              Press enquiries welcome — interviews, features, reviews and podcast appearances. Get in
              touch for assets, photos and availability.
            </p>
          </div>

          <div className="reveal">
            <div
              className="p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
              style={{
                background: 'rgba(204,0,0,0.05)',
                border: '1px solid rgba(204,0,0,0.15)',
                borderRadius: '3px',
              }}
            >
              <p className="font-mono text-xs tracking-wide" style={{ color: '#888' }}>
                Press enquiries welcome — available for interviews, features, and podcast
                appearances.
              </p>
              <a
                href="mailto:diggityglover@gmail.com?subject=Press Enquiry"
                className="font-mono text-xs tracking-widest uppercase px-5 py-2.5 whitespace-nowrap transition-all duration-200"
                style={{
                  border: '1px solid rgba(204,0,0,0.4)',
                  color: '#CC0000',
                  borderRadius: '2px',
                  background: 'transparent',
                }}
              >
                Press Enquiry →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
           9. SERVICES
        ═══════════════════════════════════════════════════════ */}
      <section
        className="py-24"
        style={{ background: '#0A0A0A', borderBottom: '1px solid rgba(180,0,0,0.12)' }}
      >
        <div className="max-w-6xl mx-auto px-6">
          <div className="reveal mb-12">
            <span className="section-label block mb-3">// Services</span>
            <h2
              className="font-display font-extrabold uppercase text-white"
              style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1',
                letterSpacing: '-0.02em',
              }}
            >
              What I Offer
            </h2>
          </div>

          <div className="reveal grid grid-cols-1 md:grid-cols-3 gap-5">
            {services.map((svc, i) => (
              <div
                key={svc.title}
                className="relative p-8"
                style={{
                  background:
                    i === 1
                      ? 'linear-gradient(135deg, rgba(204,0,0,0.1) 0%, rgba(139,0,0,0.06) 100%)'
                      : '#111',
                  border:
                    i === 1 ? '1px solid rgba(204,0,0,0.35)' : '1px solid rgba(255,255,255,0.06)',
                  borderRadius: '3px',
                  paddingTop: i === 1 ? '3rem' : '2rem',
                  paddingBottom: i === 1 ? '3rem' : '2rem',
                }}
              >
                {i === 1 && (
                  <div
                    className="absolute top-0 left-0 right-0 h-px"
                    style={{
                      background:
                        'linear-gradient(to right, transparent, rgba(204,0,0,0.8), transparent)',
                    }}
                  />
                )}
                <h3
                  className="font-display font-bold text-white mb-3"
                  style={{ fontSize: '1.15rem' }}
                >
                  {svc.title}
                </h3>
                <p className="font-body text-sm leading-relaxed" style={{ color: '#888' }}>
                  {svc.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
           10. GEAR / EQUIPMENT
        ═══════════════════════════════════════════════════════ */}
      <section className="py-24" style={{ borderBottom: '1px solid rgba(180,0,0,0.12)' }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="reveal mb-4">
            <span className="section-label block mb-3">// Current Gear</span>
            <h2
              className="font-display font-extrabold uppercase text-white mb-2"
              style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                lineHeight: '1',
                letterSpacing: '-0.02em',
              }}
            >
              My Setup
            </h2>
            <p className="font-mono text-xs tracking-wide" style={{ color: '#555' }}>
              The core kit and tools behind the sound.
            </p>
          </div>

          <div className="reveal grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
            {gearSlots.map((gear) => (
              <div
                key={gear.category}
                className="relative p-6 text-center"
                style={{
                  background: '#111',
                  border: '1px solid rgba(204,0,0,0.25)',
                  borderRadius: '3px',
                }}
              >
                <div
                  className="font-mono text-xs tracking-widest uppercase mb-2"
                  style={{ color: '#888' }}
                >
                  {gear.category}
                </div>
                <div
                  className="font-mono text-xs px-3 py-1 inline-block"
                  style={{
                    background: 'rgba(204,0,0,0.08)',
                    border: '1px solid rgba(204,0,0,0.2)',
                    color: '#CC0000',
                    borderRadius: '2px',
                    letterSpacing: '0.08em',
                  }}
                >
                  {gear.item}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
           11. CONTACT / BOOKING
        ═══════════════════════════════════════════════════════ */}
      <section className="py-28">
        <div className="max-w-6xl mx-auto px-6">
          <div className="reveal text-center mb-14">
            <span className="section-label block mb-3">// Contact Information</span>
            <h2
              className="font-display font-extrabold uppercase text-white mb-4"
              style={{
                fontSize: 'clamp(2.2rem, 5vw, 3.5rem)',
                lineHeight: '1',
                letterSpacing: '-0.03em',
              }}
            >
              Book &amp; Hire
            </h2>
            <p className="font-body text-base max-w-lg mx-auto" style={{ color: '#888' }}>
              Available for session work, touring, remote drum production and brand partnerships.
            </p>
          </div>

          <div className="reveal max-w-2xl mx-auto">
            {/* Contact details */}
            <div
              className="p-5 mb-4"
              style={{
                background: '#111',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: '3px',
              }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { label: 'Name', value: 'Diggity Glover' },
                  { label: 'Location', value: 'Manchester, UK' },
                  { label: 'Website', value: 'diggityglover.com' },
                  { label: 'Linktree', value: 'linktr.ee/diggityglover' },
                ].map((item) => (
                  <div key={item.label}>
                    <div
                      className="font-mono text-xs tracking-widest uppercase mb-1"
                      style={{ color: '#555' }}
                    >
                      {item.label}
                    </div>
                    <div className="font-body text-sm text-white">{item.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Email */}
            <div
              className="flex items-center justify-between p-5 mb-4"
              style={{
                background: '#111',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: '3px',
              }}
            >
              <div>
                <div
                  className="font-mono text-xs tracking-widest uppercase mb-1"
                  style={{ color: '#555' }}
                >
                  Email
                </div>
                <a
                  href="mailto:diggityglover@gmail.com"
                  className="font-body text-white hover:text-red-400 transition-colors"
                  style={{ fontSize: '1rem' }}
                >
                  diggityglover@gmail.com
                </a>
              </div>
              <a
                href="mailto:diggityglover@gmail.com"
                className="font-mono text-xs tracking-widest uppercase px-4 py-2 transition-all duration-200"
                style={{
                  border: '1px solid rgba(204,0,0,0.4)',
                  color: '#CC0000',
                  borderRadius: '2px',
                }}
              >
                Send →
              </a>
            </div>

            {/* CTA buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
              <a
                href="https://fiverr.com/diggityglover"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary py-4 text-center font-semibold tracking-wide text-sm"
              >
                Hire via Fiverr
              </a>
              <a
                href="https://linktr.ee/diggityglover"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline py-4 text-center font-semibold tracking-wide text-sm"
              >
                Linktree
              </a>
            </div>

            {/* Social icons row */}
            <div className="flex items-center justify-center gap-5 flex-wrap">
              {[
                {
                  label: 'Instagram',
                  href: 'https://www.instagram.com/diggity.glover/',
                  icon: 'IG',
                },
                { label: 'TikTok', href: 'https://www.tiktok.com/@diggity.glover', icon: 'TT' },
                { label: 'YouTube', href: 'https://www.youtube.com/@Diggity.Glover', icon: 'YT' },
                {
                  label: 'Facebook',
                  href: 'https://www.facebook.com/profile.php?id=100081772446953',
                  icon: 'FB',
                },
              ].map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1.5 group"
                >
                  <div
                    className="w-11 h-11 flex items-center justify-center font-mono text-xs font-bold transition-all duration-200"
                    style={{
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '3px',
                      color: '#888',
                      background: '#111',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLDivElement).style.borderColor = '#CC0000';
                      (e.currentTarget as HTMLDivElement).style.color = '#CC0000';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLDivElement).style.borderColor =
                        'rgba(255,255,255,0.1)';
                      (e.currentTarget as HTMLDivElement).style.color = '#888';
                    }}
                  >
                    {social.icon}
                  </div>
                  <span className="font-mono text-xs" style={{ color: '#444' }}>
                    {social.label}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer strip */}
      <div
        className="py-6 text-center"
        style={{ borderTop: '1px solid rgba(204,0,0,0.1)', background: '#0A0A0A' }}
      >
        <p className="font-mono text-xs" style={{ color: '#333' }}>
          © {new Date().getFullYear()} Diggity Glover · EPK · diggityglover.com
        </p>
      </div>
    </div>
  );
}
